import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Plus, LogOut, Edit, Trash2, Clock, Ship, Search, Package
} from "lucide-react";
import { toast } from "sonner";

const API_URL = "/api";

interface Shipment {
    tracking_number: string;
    sender_name: string;
    receiver_name: string;
    origin: string;
    destination: string;
    current_status: string;
    estimated_delivery: string;
    created_at: string;
}

const Dashboard = () => {
    const navigate = useNavigate();
    const [shipments, setShipments] = useState<Shipment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const username = localStorage.getItem("admin_username");

    useEffect(() => {
        // Check if logged in
        if (!localStorage.getItem("admin_logged_in")) {
            navigate("/admin");
            return;
        }

        fetchShipments();
    }, [navigate]);

    const fetchShipments = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/shipments.php`, {
                credentials: "include",
            });

            const data = await response.json();

            if (data.success) {
                setShipments(data.data);
            } else {
                toast.error("Failed to load shipments");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("Failed to load shipments");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_logged_in");
        localStorage.removeItem("admin_username");
        toast.success("Logged out successfully");
        navigate("/admin");
    };

    const handleDelete = async (trackingNumber: string) => {
        if (!confirm(`Are you sure you want to delete shipment ${trackingNumber}?`)) {
            return;
        }

        try {
            const response = await fetch(
                `${API_URL}/shipments.php?tracking_number=${trackingNumber}`,
                {
                    method: "DELETE",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (data.success) {
                toast.success("Shipment deleted successfully");
                fetchShipments();
            } else {
                toast.error(data.error || "Failed to delete shipment");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete shipment");
        }
    };

    const filteredShipments = shipments.filter((shipment) =>
        Object.values(shipment).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getStatusColor = (status: string) => {
        const statusLower = status.toLowerCase();
        if (statusLower.includes("delivered")) return "bg-green-600";
        if (statusLower.includes("transit")) return "bg-blue-600";
        if (statusLower.includes("processing")) return "bg-yellow-600";
        if (statusLower.includes("customs")) return "bg-orange-600";
        return "bg-gray-600";
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="border-b bg-white sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                                <Ship className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                                <p className="text-sm text-muted-foreground">Welcome, {username}</p>
                            </div>
                        </div>
                        <Button onClick={handleLogout} variant="outline">
                            <LogOut className="h-4 w-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Total Shipments
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3">
                                <Package className="h-8 w-8 text-primary" />
                                <span className="text-3xl font-bold">{shipments.length}</span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                In Transit
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3">
                                <Ship className="h-8 w-8 text-blue-600" />
                                <span className="text-3xl font-bold">
                                    {shipments.filter((s) => s.current_status.toLowerCase().includes("transit")).length}
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Delivered
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3">
                                <Clock className="h-8 w-8 text-green-600" />
                                <span className="text-3xl font-bold">
                                    {shipments.filter((s) => s.current_status.toLowerCase().includes("delivered")).length}
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Actions */}
                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                            <div className="relative flex-1 w-full">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <Input
                                    placeholder="Search shipments..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Button onClick={() => navigate("/admin/add-shipment")}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add New Shipment
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Shipments Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>All Shipments</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-center py-12">Loading shipments...</div>
                        ) : filteredShipments.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                {searchQuery ? "No shipments found matching your search" : "No shipments yet. Create your first shipment!"}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Tracking Number</TableHead>
                                            <TableHead>Sender</TableHead>
                                            <TableHead>Receiver</TableHead>
                                            <TableHead>Route</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>ETA</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredShipments.map((shipment) => (
                                            <TableRow key={shipment.tracking_number}>
                                                <TableCell className="font-mono font-semibold">
                                                    {shipment.tracking_number}
                                                </TableCell>
                                                <TableCell>{shipment.sender_name}</TableCell>
                                                <TableCell>{shipment.receiver_name}</TableCell>
                                                <TableCell className="text-sm">
                                                    {shipment.origin} → {shipment.destination}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={getStatusColor(shipment.current_status)}>
                                                        {shipment.current_status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{shipment.estimated_delivery || "TBD"}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex gap-2 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => navigate(`/admin/status/${shipment.tracking_number}`)}
                                                        >
                                                            <Clock className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => navigate(`/admin/edit-shipment/${shipment.tracking_number}`)}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="destructive"
                                                            onClick={() => handleDelete(shipment.tracking_number)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
